import { GameSearchResult } from '../../../../../components/data-type-widgets/data-type/shared/lookup/game-search-modal/game-search.model';
import { FieldConfigDTO } from '../../../../../system/system-dtos';
import {
  WageringGameInstance,
  WageringGameRow,
  WageringGroupRow,
  WageringProfileDetails,
} from './wagering-profile.model';

export function normalizeDetails(data: any): WageringProfileDetails {
  const src = data?.details ?? data ?? {};
  const gamesOf = (key: string): WageringGameRow[] =>
    structuredClone(src[key]?.games ?? (Array.isArray(src[key]) ? src[key] : []));

  return {
    id: src.id,
    name: src.name ?? '',
    exclusive: !!src.exclusive,
    dateCreated: src.dateCreated,
    dateUpdated: src.dateUpdated,
    groups: { groups: structuredClone(src.groups?.groups ?? src.groups ?? []) },
    games: { games: gamesOf('games') },
    bonusExclude: { games: gamesOf('bonusExclude') },
    disabledGames: { games: gamesOf('disabledGames') },
  };
}

export function toGameInstance(res: GameSearchResult | any): WageringGameInstance {
  return {
    id: res.id,
    name: res.name,
    logoUrl: res.logoUrl || res.imageUrl,
    provider: res.provider || res.studio,
    studioName: res.studioName || res.studio,
    status: res.status || 'ACTIVE',
    gameId: res.gameId,
    cohortId: res.cohortId,
  };
}

export function toGroupRef(res: any): { id?: string; label?: string } {
  if (!res) {
    return {};
  }
  // Already a group ref (e.g. after lookup retValue).
  if (res.label != null || (res.id != null && res.name == null && !res.code)) {
    return { id: res.id != null ? String(res.id) : res.id, label: res.label };
  }
  return {
    id: res.id != null ? String(res.id) : res.code != null ? String(res.code) : undefined,
    label: res.label || res.name || res.code || (res.id != null ? String(res.id) : undefined),
  };
}

export function createEmptyGroupRow(template: Partial<WageringGroupRow> = {}, displayOrder = 10): WageringGroupRow {
  return {
    group: template.group ? { ...template.group } : { id: undefined, label: '' },
    club: Number(template.club) || 0,
    bronze: Number(template.bronze) || 0,
    silver: Number(template.silver) || 0,
    gold: Number(template.gold) || 0,
    platinum: Number(template.platinum) || 0,
    displayOrder: Number(template.displayOrder) || displayOrder,
  };
}

/** Recursively find the nested-table / object field named `groups`. */
export function findGroupsField(fields?: FieldConfigDTO[] | null): FieldConfigDTO | undefined {
  if (!fields?.length) {
    return undefined;
  }
  for (const field of fields) {
    if (field.name === 'groups') {
      return field;
    }
    const nested = findGroupsField(field.innerFields) || findGroupsField(field.nestedTable);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

/**
 * Find the lookup field used to pick a wagering group row's `group`.
 * Prefer `innerFields` (full edit config with `lookup`) over `nestedTable` (often display-only).
 */
export function findGroupLookupField(fields?: FieldConfigDTO[] | null): FieldConfigDTO | undefined {
  if (!fields?.length) {
    return undefined;
  }

  const groups = findGroupsField(fields);
  if (groups) {
    const fromInner = (groups.innerFields ?? []).find((f) => f.name === 'group' && !!f.lookup);
    if (fromInner) {
      return fromInner;
    }
    const fromNested = (groups.nestedTable ?? []).find((f) => f.name === 'group' && !!f.lookup);
    if (fromNested) {
      return fromNested;
    }
    const bare = [...(groups.innerFields ?? []), ...(groups.nestedTable ?? [])].find((f) => f.name === 'group');
    if (bare?.lookup) {
      return bare;
    }
  }

  for (const field of fields) {
    if (field.name === 'group' && field.lookup) {
      return field;
    }
    const nested = findGroupLookupField(field.innerFields) || findGroupLookupField(field.nestedTable);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}
